import React, { useEffect, useState, useRef, useContext, Component } from 'react';
import { X, Copy, Check, Mail, Share2, Loader2, Globe, Lock, ExternalLink } from 'lucide-react';
import { semanticBrandingService } from '../../services/semanticBranding';
import { sharingService } from '../../services/sharing';
import { ToastContext } from '../../App';
interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  isPublic: boolean;
  onVisibilityChange: (isPublic: boolean) => void;
}
const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  documentId,
  isPublic,
  onVisibilityChange
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isChangingVisibility, setIsChangingVisibility] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const {
    addToast
  } = useContext(ToastContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const shareUrl = `${window.location.origin}/relatorio/${documentId}`;
  // Focus the input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.select();
      }, 100);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const handleCopyLink = () => {
    if (inputRef.current) {
      inputRef.current.select();
      document.execCommand('copy');
      // As a fallback, also try the clipboard API
      navigator.clipboard.writeText(shareUrl).catch(err => {
        console.error('Failed to copy with clipboard API:', err);
      });
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };
  const handleVisibilityChange = async () => {
    setIsChangingVisibility(true);
    try {
      const newVisibility = isPublic ? 'private' : 'public';
      await semanticBrandingService.updateVisibility(documentId, newVisibility);
      onVisibilityChange(!isPublic);
    } catch (error) {
      console.error('Failed to update visibility:', error);
    } finally {
      setIsChangingVisibility(false);
    }
  };
  const handleSendEmail = async () => {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail)) {
      setEmailError('Por favor, insira um e-mail válido');
      return;
    }
    // Check if report is public
    if (!isPublic) {
      addToast('error', 'O relatório precisa ser público para ser compartilhado por e-mail');
      return;
    }
    setIsSendingEmail(true);
    setEmailError('');
    try {
      await sharingService.sendReportByEmail(documentId, recipientEmail);
      addToast('success', 'E-mail enviado com sucesso!');
      setRecipientEmail('');
      setShowEmailInput(false);
    } catch (error) {
      console.error('Failed to send email:', error);
      addToast('error', error.response?.data?.message || 'Erro ao enviar e-mail. Tente novamente.');
    } finally {
      setIsSendingEmail(false);
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1194">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative" data-id="element-1195">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700" data-id="element-1196">
          <X size={20} data-id="element-1197" />
        </button>
        <h3 className="text-xl font-semibold mb-4 text-gray-900" data-id="element-1198">
          Compartilhar relatório
        </h3>
        {/* Visibility status */}
        <div className="mb-6 p-4 rounded-lg bg-gray-50" data-id="element-1199">
          <div className="flex items-center justify-between mb-4" data-id="element-1200">
            <div className="flex items-center" data-id="element-1201">
              {isPublic ? <Globe size={20} className="text-green-600 mr-2" data-id="element-1202" /> : <Lock size={20} className="text-gray-600 mr-2" data-id="element-1203" />}
              <span className="font-medium text-gray-900" data-id="element-1204">
                {isPublic ? 'Relatório público' : 'Relatório privado'}
              </span>
            </div>
            <button onClick={handleVisibilityChange} disabled={isChangingVisibility} className={`px-4 py-2 rounded-md transition-colors ${isChangingVisibility ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : isPublic ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-[#1649ff] text-white hover:bg-blue-700'}`} data-id="element-1205">
              {isChangingVisibility ? 'Alterando...' : isPublic ? 'Tornar privado' : 'Tornar público'}
            </button>
          </div>
          <p className="text-sm text-gray-600" data-id="element-1206">
            {isPublic ? 'Qualquer pessoa com o link pode visualizar este relatório sem precisar fazer login.' : 'Apenas você pode visualizar este relatório. Torne-o público para compartilhar.'}
          </p>
        </div>
        {isPublic && <>
            {/* Share link */}
            <div className="mb-4" data-id="element-1207">
              <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-1208">
                Link para compartilhamento:
              </label>
              <div className="flex" data-id="element-1209">
                <input ref={inputRef} type="text" value={shareUrl} readOnly className="flex-grow px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-1 focus:ring-[#1649ff] focus:border-[#1649ff] text-gray-900" onClick={e => e.currentTarget.select()} data-id="element-1210" />
                <button onClick={handleCopyLink} className="px-3 py-2 bg-[#1649ff] text-white rounded-r-md hover:bg-blue-700 flex items-center" data-id="element-1211">
                  {isCopied ? <Check size={16} className="mr-1" data-id="element-1212" /> : <Copy size={16} className="mr-1" data-id="element-1213" />}
                  {isCopied ? 'Copiado' : 'Copiar'}
                </button>
              </div>
            </div>
            {/* Share buttons */}
            <div className="mb-6" data-id="element-1214">
              <p className="text-sm font-medium text-gray-900 mb-3" data-id="element-1215">
                Compartilhar em:
              </p>
              <div className="flex space-x-3" data-id="element-1216">
                <a href={`https://wa.me/?text=${encodeURIComponent(`Confira esta análise semântica: ${shareUrl}`)}`} target="_blank" rel="noopener noreferrer" className="flex items-center px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600" data-id="element-1217">
                  WhatsApp
                  <ExternalLink size={14} className="ml-1" data-id="element-1218" />
                </a>
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-1219">
                  LinkedIn
                  <ExternalLink size={14} className="ml-1" data-id="element-1220" />
                </a>
                {/* Email button */}
                <button onClick={() => setShowEmailInput(!showEmailInput)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center justify-center" data-id="element-1221">
                  <Mail size={18} className="mr-2" data-id="element-1222" />
                  Email
                </button>
              </div>
            </div>
            {/* Email input section */}
            {showEmailInput && <div className="mb-6 p-4 bg-gray-50 rounded-lg" data-id="element-1223">
                <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1224">
                  Enviar relatório por e-mail
                </h3>
                <div className="space-y-3" data-id="element-1225">
                  <div data-id="element-1226">
                    <input type="email" value={recipientEmail} onChange={e => {
                setRecipientEmail(e.target.value);
                setEmailError('');
              }} placeholder="email@exemplo.com" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" disabled={isSendingEmail} data-id="element-1227" />
                    {emailError && <p className="text-sm text-red-600 mt-1" data-id="element-1228">{emailError}</p>}
                  </div>
                  <div className="flex space-x-2" data-id="element-1229">
                    <button onClick={handleSendEmail} disabled={isSendingEmail || !recipientEmail} className="flex-1 px-4 py-2 bg-[#1649ff] text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center" data-id="element-1230">
                      {isSendingEmail ? <>
                          <Loader2 size={16} className="mr-2 animate-spin" data-id="element-1231" />
                          Enviando...
                        </> : <>
                          <Mail size={16} className="mr-2" data-id="element-1232" />
                          Enviar
                        </>}
                    </button>
                    <button onClick={() => {
                setShowEmailInput(false);
                setRecipientEmail('');
                setEmailError('');
              }} disabled={isSendingEmail} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200" data-id="element-1233">
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>}
          </>}
        {/* Close button */}
        <div className="mt-6 flex justify-end" data-id="element-1234">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200" data-id="element-1235">
            Fechar
          </button>
        </div>
      </div>
    </div>;
};
export default ShareModal;